import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency, CurrencyToggle } from "@/lib/currency";
import { toast } from "sonner";
import { Check, Crown, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("canceled")) {
      toast.info("Pago cancelado. Puedes intentarlo cuando quieras.");
    }
    (async () => {
      try {
        const { data } = await api.get("/billing/plans");
        setPlans(data.plans || []);
      } catch (e) {
        toast.error("No se pudieron cargar los planes");
      }
    })();
  }, [params]);

  const onSelect = async (plan) => {
    if (!plan.package_id) {
      // Free plan
      if (!user) navigate("/signup");
      else navigate("/dashboard");
      return;
    }
    if (!user) {
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }
    setLoadingId(plan.id);
    try {
      const origin_url = window.location.origin;
      // Pick the user's most recent project to associate with the unlock
      let project_id = "";
      try {
        const { data: pj } = await api.get("/projects");
        project_id = (pj?.projects || [])[0]?.project_id || "";
      } catch {
        /* ignore */
      }
      const { data } = await api.post("/billing/checkout", {
        package_id: plan.package_id,
        origin_url,
        project_id,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No fue posible iniciar el pago");
      setLoadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="pricing-page">
      <header className="border-b border-rule bg-paper/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-baseline gap-0.5">
            <span className="font-serif italic text-2xl text-copper">una</span><span className="font-serif text-2xl text-ink">TESIS</span>
          </Link>
          <Link
            to={user ? "/dashboard" : "/"}
            className="inline-flex items-center gap-1.5 text-[13px] text-ink-soft hover:text-forest transition-colors"
          >
            <ArrowLeft size={14} /> {user ? "Volver al escritorio" : "Volver al inicio"}
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">Planes unaTESIS</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-ink leading-tight text-balance">
            Empieza gratis. Sube a Premium cuando lo necesites.
          </h1>
          <p className="mt-4 text-[15px] text-ink-soft leading-relaxed">
            Sin contratos largos. Cancela cuando quieras. Mismos pagos seguros que usan miles de SaaS en todo el mundo.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="text-[11px] tracking-[0.18em] uppercase text-ink-soft font-medium">Mostrar precios en</span>
            <CurrencyToggle testid="pricing-currency-toggle" />
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              currentPlan={user?.plan}
              loading={loadingId === plan.id}
              onSelect={() => onSelect(plan)}
            />
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-ink-soft max-w-xl mx-auto">
          Pagos procesados de forma segura con Stripe (modo de prueba). En el modo test puedes usar la tarjeta{" "}
          <code className="bg-paper-2 px-1.5 py-0.5 rounded text-[11px]">4242 4242 4242 4242</code>, cualquier fecha futura y CVC.
        </p>
      </main>
    </div>
  );
}

function PlanCard({ plan, currentPlan, loading, onSelect }) {
  const isPremium = plan.id === "premium";
  const isActive = currentPlan === plan.id;
  const { currency, format } = useCurrency();
  const isFree = !plan.price_usd || Number(plan.price_usd) === 0;
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-2xl p-7 sm:p-8 flex flex-col ${
        plan.highlight
          ? "border border-forest/20 bg-white shadow-[0_30px_60px_-25px_rgba(39,59,44,0.2)]"
          : "border border-rule bg-white/60"
      }`}
    >
      {plan.highlight && (
        <span aria-hidden className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-copper to-transparent" />
      )}

      <div className="flex items-center gap-2">
        {isPremium ? (
          <span className="grid place-items-center h-9 w-9 rounded-full bg-forest text-paper">
            <Crown size={15} className="text-[#E0C58A]" />
          </span>
        ) : (
          <span className="grid place-items-center h-9 w-9 rounded-full border border-rule text-forest">
            <Sparkles size={15} />
          </span>
        )}
        <h3 className="font-serif text-2xl text-ink">{plan.name}</h3>
        {isActive && (
          <span className="ml-auto text-[10px] tracking-[0.18em] uppercase text-copper font-medium">Tu plan</span>
        )}
      </div>

      <div className="mt-5 flex items-baseline gap-1.5">
        {isFree ? (
          <>
            <span className="font-serif text-5xl text-ink leading-none">$0</span>
            <span className="text-[13px] text-ink-soft">{plan.billing}</span>
          </>
        ) : (
          <>
            <span
              data-testid={`plan-price-${plan.id}`}
              data-currency={currency}
              className="font-serif text-5xl text-ink leading-none"
            >
              {format(plan.price_usd)}
            </span>
            <span className="text-[13px] text-ink-soft">{plan.billing}</span>
          </>
        )}
      </div>
      <p className="mt-2 text-[13.5px] text-ink-soft">{plan.tagline}</p>

      <ul className="mt-6 flex flex-col gap-3">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <span className="mt-0.5 grid place-items-center h-5 w-5 rounded-full bg-forest text-paper shrink-0">
              <Check size={11} strokeWidth={3} />
            </span>
            <span className="text-[14px] text-ink/90">{f}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`plan-cta-${plan.id}`}
        onClick={onSelect}
        disabled={loading || isActive}
        className={`mt-8 group inline-flex items-center justify-center gap-2 py-3.5 rounded-full text-[14px] font-medium transition-all disabled:opacity-60 ${
          plan.highlight
            ? "bg-forest text-paper hover:bg-forest-dark shadow-sm hover:shadow-md hover:-translate-y-[1px]"
            : "bg-paper-2 text-ink hover:bg-rule"
        }`}
      >
        {loading ? "Redirigiendo a Stripe…" : isActive ? "Ya estás en este plan" : (isFree ? plan.cta : `${plan.cta} — ${format(plan.price_usd)}`)}
        {!loading && !isActive && <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />}
      </button>
    </div>
  );
}
